import React from 'react';
import { X, Clock, MapPin, User, Calendar, FileText, Building, Phone, Edit, Trash2 } from 'lucide-react';
import { Event } from '../../../types';

interface EventDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: Event | null;
  language: 'en' | 'ar';
  strings: any;
  getEventTypeName: (typeId: string) => string;
  getPlaceTypeName: (typeId: string) => string;
  onEdit?: (event: Event) => void;
  onDelete?: (eventId: string) => void;
  isDeleting?: boolean;
}

const EventDetailsModal: React.FC<EventDetailsModalProps> = ({
  isOpen,
  onClose,
  event,
  language,
  strings,
  getEventTypeName,
  getPlaceTypeName,
  onEdit,
  onDelete,
  isDeleting = false
}) => {
  if (!isOpen || !event) return null;

  // Parse event date using the same logic as CalendarView and EventCard
  const parseEventDate = (dateString: string): Date | null => {
    if (!dateString) return null;
    
    try {
      // Check for DD-MM-YYYY format (European format used in your database)
      if (dateString.match(/^\d{1,2}-\d{1,2}-\d{4}$/)) {
        const parts = dateString.split('-');
        if (parts.length === 3) {
          const day = parseInt(parts[0]);   // First part is day
          const month = parseInt(parts[1]); // Second part is month
          const year = parseInt(parts[2]);  // Third part is year
          return new Date(year, month - 1, day); // Subtract 1 only when creating Date object
        }
      } else if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // Already in YYYY-MM-DD format
        return new Date(dateString);
      } else {
        // Try direct parsing for other formats
        const parsedDate = new Date(dateString);
        return !isNaN(parsedDate.getTime()) ? parsedDate : null;
      }
    } catch (error) {
      console.warn('Error parsing date:', dateString, error);
      return null;
    }
    
    return null;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return language === 'ar' ? 'التاريخ غير محدد' : 'Date not specified';
    
    const parsedDate = parseEventDate(dateString);
    if (!parsedDate) return language === 'ar' ? 'تاريخ غير صحيح' : 'Invalid Date';
    
    return parsedDate.toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-GB', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString?: string) => {
    if (!timeString || timeString === 'الوقت غير محدد' || timeString === 'Time not specified') {
      return language === 'ar' ? 'الوقت غير محدد' : 'Time not specified';
    }
    return timeString;
  };

  const getStatusColor = (status: 'scheduled' | 'completed' | 'cancelled' | 'postponed' | number) => {
    if (typeof status === 'string') {
      switch (status) {
        case 'scheduled':
          return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
        case 'completed':
          return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
        case 'cancelled':
          return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
        case 'postponed':
          return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
        default:
          return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300';
      }
    } else {
      switch (status) {
        case 1: // Upcoming
          return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
        case 2: // Completed
          return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
        case 3: // Cancelled
          return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
        default:
          return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300';
      }
    }
  };

  const getStatusText = (status: 'scheduled' | 'completed' | 'cancelled' | 'postponed' | number) => {
    if (typeof status === 'string') {
      switch (status) {
        case 'scheduled':
          return language === 'ar' ? 'مجدول' : 'Scheduled';
        case 'completed':
          return language === 'ar' ? 'مكتمل' : 'Completed';
        case 'cancelled':
          return language === 'ar' ? 'ملغي' : 'Cancelled';
        case 'postponed':
          return language === 'ar' ? 'مؤجل' : 'Postponed';
        default:
          return language === 'ar' ? 'غير محدد' : 'Unknown';
      }
    } else {
      switch (status) {
        case 1:
          return language === 'ar' ? 'قادم' : 'Upcoming';
        case 2:
          return language === 'ar' ? 'مكتمل' : 'Completed';
        case 3:
          return language === 'ar' ? 'ملغي' : 'Cancelled';
        default:
          return language === 'ar' ? 'غير محدد' : 'Unknown';
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-slate-800 dark:text-white">
              {language === 'ar' ? 'تفاصيل الحدث' : 'Event Details'}
            </h2>
            <div className="flex items-center gap-2">
              {onEdit && (
                <button
                  onClick={() => {
                    if (event) {
                      onEdit(event);
                      onClose();
                    }
                  }}
                  className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                  title={language === 'ar' ? 'تعديل' : 'Edit'}
                >
                  <Edit className="w-5 h-5 text-slate-500" />
                </button>
              )}
              {onDelete && (
                <button
                  onClick={() => {
                    if (event) {
                      onDelete(event.id);
                    }
                  }}
                  disabled={isDeleting}
                  className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors disabled:opacity-50"
                  title={language === 'ar' ? 'حذف' : 'Delete'}
                >
                  <Trash2 className="w-5 h-5 text-red-500" />
                </button>
              )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Event Title and Status */}
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-2">
                {event.legalFileTitle || (language === 'ar' ? 'عنوان غير محدد' : 'Untitled Event')}
              </h3>
              <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(event.status)}`}>
                {getStatusText(event.status)}
              </span>
            </div>
          </div>

          {/* Event Details Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Date and Time */}
            <div className="lg:col-span-1 space-y-4">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-slate-500 dark:text-slate-400" />
                <div>
                  <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
                    {language === 'ar' ? 'التاريخ' : 'Date'}
                  </p>
                  <p className="text-slate-800 dark:text-white">
                    {formatDate(event.date)}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-slate-500 dark:text-slate-400" />
                <div>
                  <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
                    {language === 'ar' ? 'الوقت' : 'Time'}
                  </p>
                  <p className="text-slate-800 dark:text-white">
                    {formatTime(event.time)}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-slate-500 dark:text-slate-400" />
                <div>
                  <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
                    {language === 'ar' ? 'المكان' : 'Location'}
                  </p>
                  <p className="text-slate-800 dark:text-white">
                    {event.placeName || (event.placeTypeID ? getPlaceTypeName(event.placeTypeID) : (language === 'ar' ? 'مكان غير محدد' : 'Location not specified'))}
                  </p>
                </div>
              </div>
            </div>

            {/* Client and Office Info */}
            <div className="lg:col-span-2 space-y-4">
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-slate-500 dark:text-slate-400" />
                <div>
                  <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
                    {language === 'ar' ? 'العميل' : 'Client'}
                  </p>
                  <p className="text-slate-800 dark:text-white">
                    {event.clientName || (language === 'ar' ? 'عميل غير محدد' : 'Client not specified')}
                  </p>
                  {event.clientPhoneNumber && (
                    <div className="flex items-center gap-2 mt-1">
                      <Phone className="w-4 h-4 text-slate-400" />
                      <span className="text-sm text-slate-500 dark:text-slate-400">
                        {event.clientPhoneNumber}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Building className="w-5 h-5 text-slate-500 dark:text-slate-400" />
                <div>
                  <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
                    {language === 'ar' ? 'المكتب' : 'Office'}
                  </p>
                  <p className="text-slate-800 dark:text-white">
                    {event.officeName || (language === 'ar' ? 'مكتب غير محدد' : 'Office not specified')}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-slate-500 dark:text-slate-400" />
                <div>
                  <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
                    {language === 'ar' ? 'نوع الحدث' : 'Event Type'}
                  </p>
                  <p className="text-slate-800 dark:text-white">
                    {event.eventTypeID ? getEventTypeName(event.eventTypeID) : (language === 'ar' ? 'غير محدد' : 'Not specified')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          {event.notes && (
            <div>
              <h4 className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                {language === 'ar' ? 'ملاحظات' : 'Notes'}
              </h4>
              <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                <p className="text-slate-800 dark:text-white whitespace-pre-wrap">
                  {event.notes}
                </p>
              </div>
            </div>
          )}

          {/* Reminder Status */}
          {event.hasReminder === 1 && (
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                <span className="text-sm text-blue-800 dark:text-blue-300">
                  {language === 'ar' ? 'تم تعيين تذكير لهذا الحدث' : 'Reminder is set for this event'}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Modal Actions */}
        <div className="p-6 border-t border-slate-200 dark:border-slate-700">
          <div className="flex items-center justify-end gap-3">
            {onEdit && (
              <button
                onClick={() => {
                  if (event) {
                    onEdit(event);
                    onClose();
                  }
                }}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <Edit className="w-4 h-4" />
                {language === 'ar' ? 'تعديل' : 'Edit'}
              </button>
            )}
            {onDelete && (
              <button
                onClick={() => {
                  if (event) {
                    onDelete(event.id);
                  }
                }}
                disabled={isDeleting}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                {language === 'ar' ? 'حذف' : 'Delete'}
              </button>
            )}
            <button
              onClick={onClose}
              className="px-6 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors"
            >
              {language === 'ar' ? 'إغلاق' : 'Close'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventDetailsModal;
